/**
 * PaidChannelUI — Creator + Subscriber UI for paid community channels.
 *
 * Three components exported:
 *   - usePaidChannelStatus()   — hook returning { enabled, inviteOnly, loading }
 *   - MonetizeChannelModal     — owner-side: apply for paid status with 1-3 tiers
 *   - SubscribeChannelModal    — viewer-side: pick tier + receive USDC payment instructions
 *   - PaidChannelLockedPanel   — shown in place of messages when user isn't subscribed
 *
 * All three are hidden when the `PAID_CHANNELS_ENABLED` flag is OFF.
 * The admin master switch lives at Admin Center → Paid Channels.
 */

import { useEffect, useState, useCallback } from 'react';
import { useAccount, useWalletClient, useSwitchChain, useChainId, useReadContracts } from 'wagmi';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { X, Lock, DollarSign, Sparkles, Plus, Trash2, Loader2, AlertTriangle, Check, Copy, Shield, TrendingUp, Crown, Zap, RefreshCw } from 'lucide-react';
import { getAutoRenewSpender, enableAutoRenew } from '../services/api';

const API = import.meta.env.VITE_API_URL || '/api';

function authHeaders() {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('authToken')}` };
}

// ─── Hook: PaidChannels feature flag ──────────────────────────────────────

export function usePaidChannelStatus() {
  const [state, setState] = useState({ enabled: false, inviteOnly: true, loading: true });
  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/paid-channels/status`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled && d) setState({ enabled: !!d.enabled, inviteOnly: !!d.inviteOnly, loading: false }); })
      .catch(() => { if (!cancelled) setState(s => ({ ...s, loading: false })); });
    return () => { cancelled = true; };
  }, []);
  return state;
}

// Hook returning my pending+approved+rejected applications, keyed by channelId
export function useMyPaidApplications() {
  const [apps, setApps] = useState({});
  const refresh = useCallback(() => {
    fetch(`${API}/paid-channels/my-applications`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d?.applications) return;
        const byChannel = {};
        for (const a of d.applications) {
          // Keep the most recent application per channel
          if (!byChannel[a.channelId] || new Date(a.submittedAt) > new Date(byChannel[a.channelId].submittedAt)) {
            byChannel[a.channelId] = a;
          }
        }
        setApps(byChannel);
      })
      .catch(() => {});
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  return { apps, refresh };
}

// ─── Monetize Channel Modal (channel owner POV) ───────────────────────────

const TIER_PRESETS = [
  { name: 'Supporter', price: 9.99, accent: 'emerald', icon: Sparkles, perks: ['Access to premium messages', 'Subscriber-only reactions'] },
  { name: 'VIP', price: 29.99, accent: 'amber', icon: Crown, perks: ['Everything in Supporter', 'Direct DMs with creator', 'Weekly strategy AMAs'] },
  { name: 'Inner Circle', price: 99.99, accent: 'purple', icon: Zap, perks: ['Everything in VIP', 'Real-time trade alerts', '1:1 monthly call'] },
];

export function MonetizeChannelModal({ channelId, channelName, onClose, onSubmitted }) {
  const [eligibility, setEligibility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tiers, setTiers] = useState([{ ...TIER_PRESETS[0], perks: [...TIER_PRESETS[0].perks] }]);
  const [description, setDescription] = useState('');
  const [sampleContent, setSampleContent] = useState('');
  const [refundPolicy, setRefundPolicy] = useState('Pro-rated refunds within 7 days of subscription start');
  const [creatorPayoutWallet, setCreatorPayoutWallet] = useState('');
  const { address: connectedAddr } = useAccount();
  const [acceptedDisclaimer, setAcceptedDisclaimer] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [step, setStep] = useState(1);

  useEffect(() => {
    fetch(`${API}/paid-channels/check-eligibility`, { method: 'POST', headers: authHeaders() })
      .then(r => r.json())
      .then(d => { setEligibility(d); setLoading(false); })
      .catch(() => { setLoading(false); setError('Could not check eligibility'); });
  }, []);

  function addTier() {
    if (tiers.length >= 3) return;
    const next = TIER_PRESETS[tiers.length];
    setTiers([...tiers, { ...next, perks: [...next.perks] }]);
  }
  function removeTier(i) {
    setTiers(tiers.filter((_, idx) => idx !== i));
  }
  function updateTier(i, field, val) {
    setTiers(tiers.map((t, idx) => idx === i ? { ...t, [field]: val } : t));
  }
  function updatePerk(tierIdx, perkIdx, val) {
    setTiers(tiers.map((t, idx) => idx === tierIdx ? { ...t, perks: t.perks.map((p, pi) => pi === perkIdx ? val : p) } : t));
  }
  function addPerk(tierIdx) {
    setTiers(tiers.map((t, idx) => idx === tierIdx ? { ...t, perks: [...t.perks, ''] } : t));
  }
  function removePerk(tierIdx, perkIdx) {
    setTiers(tiers.map((t, idx) => idx === tierIdx ? { ...t, perks: t.perks.filter((_, pi) => pi !== perkIdx) } : t));
  }

  async function submit() {
    setError(null);
    // Validate
    if (!description.trim() || description.trim().length < 30) {
      setError('Channel description must be at least 30 characters');
      return;
    }
    for (const t of tiers) {
      if (!t.name?.trim()) { setError('All tiers need a name'); return; }
      if (!t.price || t.price < 1 || t.price > 500) { setError(`Tier "${t.name}" price must be between $1 and $500`); return; }
      const validPerks = (t.perks || []).map(p => p?.trim()).filter(Boolean);
      if (validPerks.length < 2) { setError(`Tier "${t.name}" needs at least 2 perks`); return; }
    }
    if (!/^0x[a-fA-F0-9]{40}$/.test(creatorPayoutWallet)) {
      setError('Enter a valid Base-compatible wallet address (the 0x... address where you want to receive USDC payouts)');
      return;
    }
    if (!acceptedDisclaimer) { setError('You must accept the Creator Agreement'); return; }

    setSubmitting(true);
    try {
      const cleanTiers = tiers.map(t => ({
        name: t.name.trim(),
        price: Number(t.price),
        perks: (t.perks || []).map(p => p?.trim()).filter(Boolean),
      }));
      const res = await fetch(`${API}/paid-channels/apply`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          channelId,
          tiers: cleanTiers,
          description: description.trim(),
          sampleContent: sampleContent.trim(),
          refundPolicy: refundPolicy.trim(),
          creatorPayoutWallet: creatorPayoutWallet.trim(),
          disclaimerAccepted: true,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || 'Application failed');
      setSuccess(data);
      if (onSubmitted) onSubmitted();
    } catch (e) {
      setError(e.message);
    }
    setSubmitting(false);
  }

  if (loading) {
    return (
      <Modal onClose={onClose}>
        <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 text-amber-400 animate-spin" /></div>
      </Modal>
    );
  }

  if (success) {
    return (
      <Modal onClose={onClose}>
        <div className="text-center py-6">
          <div className="w-16 h-16 mx-auto bg-emerald-500/20 rounded-full flex items-center justify-center mb-4">
            <Check className="w-8 h-8 text-emerald-400" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Application Submitted</h2>
          <p className="text-gray-400 text-sm mb-1">Your channel <span className="text-white font-medium">{channelName}</span> is now under admin review.</p>
          <p className="text-gray-500 text-xs mb-6">We typically review within 24-48 hours. You'll get a notification once approved.</p>
          <button onClick={onClose} className="px-6 py-2.5 rounded-xl bg-amber-500 text-dark-900 font-bold text-sm">Got it</button>
        </div>
      </Modal>
    );
  }

  if (!eligibility?.eligible) {
    return (
      <Modal onClose={onClose} title="Not Eligible Yet">
        <div className="space-y-4">
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 flex gap-3">
            <Shield className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-amber-200 space-y-1">
              <p className="font-semibold text-amber-300">To protect subscribers from low-quality channels, creators need to meet these requirements first:</p>
              <ul className="list-disc pl-4 space-y-0.5">
                {eligibility?.issues?.map((iss, i) => <li key={i}>{iss}</li>)}
              </ul>
            </div>
          </div>
          <div className="bg-dark-700/40 rounded-xl p-3">
            <p className="text-xs text-gray-400 mb-2 font-medium">Your progress:</p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="text-gray-500">Tier: <span className="text-white">{eligibility?.progress?.tier || 'trial'}</span></div>
              <div className="text-gray-500">Account age: <span className="text-white">{eligibility?.progress?.accountAgeDays || 0}d</span></div>
              <div className="text-gray-500">Followers: <span className="text-white">{eligibility?.progress?.followerCount || 0}</span></div>
            </div>
          </div>
          <button onClick={onClose} className="w-full py-2.5 rounded-xl bg-dark-700 text-white text-sm font-medium">Close</button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose} title={`Monetize · ${channelName}`}>
      <div className="space-y-5">
        {/* Step indicator */}
        <div className="flex gap-2">
          {[1, 2, 3].map(s => (
            <div key={s} className={`flex-1 h-1 rounded-full ${step >= s ? 'bg-amber-400' : 'bg-dark-600'}`} />
          ))}
        </div>

        {step === 1 && (
          <>
            <div>
              <label className="block text-xs text-gray-500 mb-1.5 font-medium">What do subscribers get? <span className="text-gray-600">(30+ chars)</span></label>
              <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} maxLength={400}
                placeholder="e.g., Daily ETH/BTC swing-trade calls with entry, target, and stop. 60%+ win rate over the last 90 days."
                className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2 text-white text-sm placeholder-gray-600 focus:border-amber-400 focus:outline-none resize-none" />
              <p className="text-[10px] text-gray-600 mt-1">{description.length}/400</p>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1.5 font-medium">Sample post (optional, helps subscribers decide)</label>
              <textarea value={sampleContent} onChange={e => setSampleContent(e.target.value)} rows={2} maxLength={300}
                placeholder='e.g., "BUY $ETH @ 2840, target 3100, stop 2750 — based on 4h breakout confirmed by volume"'
                className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2 text-white text-sm placeholder-gray-600 focus:border-amber-400 focus:outline-none resize-none" />
            </div>
            <button onClick={() => setStep(2)} disabled={!description.trim() || description.trim().length < 30}
              className="w-full py-2.5 rounded-xl bg-amber-500 text-dark-900 font-bold text-sm disabled:opacity-40 transition-all">Next: Set tiers →</button>
          </>
        )}

        {step === 2 && (
          <>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-gray-500 font-medium">Subscription tiers ({tiers.length}/3)</label>
                {tiers.length < 3 && (
                  <button onClick={addTier} className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1">
                    <Plus className="w-3 h-3" /> Add tier
                  </button>
                )}
              </div>
              <div className="space-y-3">
                {tiers.map((tier, i) => (
                  <TierEditor key={i} idx={i} tier={tier}
                    onChange={(field, val) => updateTier(i, field, val)}
                    onPerkChange={(pi, v) => updatePerk(i, pi, v)}
                    onPerkAdd={() => addPerk(i)}
                    onPerkRemove={(pi) => removePerk(i, pi)}
                    onRemove={tiers.length > 1 ? () => removeTier(i) : null} />
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setStep(1)} className="px-4 py-2.5 rounded-xl bg-dark-700 text-white text-sm">← Back</button>
              <button onClick={() => setStep(3)} className="flex-1 py-2.5 rounded-xl bg-amber-500 text-dark-900 font-bold text-sm">Next: Review →</button>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <div>
              <label className="block text-xs text-gray-500 mb-1.5 font-medium">Payout wallet (Base) — required</label>
              <div className="flex gap-2">
                <input type="text" value={creatorPayoutWallet} onChange={e => setCreatorPayoutWallet(e.target.value)} maxLength={42}
                  placeholder="0x..."
                  className="flex-1 bg-dark-700 border border-dark-600 rounded-xl px-3 py-2 text-white text-xs font-mono placeholder-gray-600 focus:border-amber-400 focus:outline-none" />
                {connectedAddr && (
                  <button type="button" onClick={() => setCreatorPayoutWallet(connectedAddr)}
                    className="px-3 py-2 rounded-xl bg-amber-500/15 border border-amber-500/30 text-amber-400 text-[10px] font-bold whitespace-nowrap">
                    Use connected
                  </button>
                )}
              </div>
              <p className="text-[10px] text-gray-600 mt-1">USDC payouts (your 80%) auto-send here instantly on every subscription. Must be a Base-compatible EVM address.</p>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1.5 font-medium">Refund policy</label>
              <input type="text" value={refundPolicy} onChange={e => setRefundPolicy(e.target.value)} maxLength={120}
                className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2 text-white text-sm focus:border-amber-400 focus:outline-none" />
            </div>
            <div className="bg-dark-700/40 rounded-xl p-3 space-y-2">
              <p className="text-xs text-gray-400 font-medium">Summary</p>
              {tiers.map((t, i) => (
                <div key={i} className="flex justify-between text-xs">
                  <span className="text-white">{t.name}</span>
                  <span className="text-amber-400 font-mono">${t.price}/mo</span>
                </div>
              ))}
              <div className="border-t border-dark-600 pt-2 mt-2 text-[10px] text-gray-500">
                Platform takes 20% of subscriptions. You receive 80% of net (after on-chain fees) in USDC.
              </div>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 flex gap-2">
              <Shield className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="text-[11px] text-amber-200 space-y-1">
                <p className="font-bold text-amber-300">Admin approval required</p>
                <p>Your channel will <span className="font-bold">NOT</span> go paid immediately. The haythix team reviews every application (typically within 24-48 hours) to protect subscribers. You'll be notified once approved.</p>
              </div>
            </div>
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={acceptedDisclaimer} onChange={e => setAcceptedDisclaimer(e.target.checked)} className="mt-0.5" />
              <span className="text-xs text-gray-400">
                I agree to the <span className="text-amber-400">Creator Agreement</span>. I will post regularly (min weekly), honor my refund policy, and follow community guidelines. False or misleading claims may result in channel suspension.
              </span>
            </label>
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2 text-xs text-red-400 flex items-center gap-2">
                <AlertTriangle className="w-3.5 h-3.5" /> {error}
              </div>
            )}
            <div className="flex gap-2">
              <button onClick={() => setStep(2)} className="px-4 py-2.5 rounded-xl bg-dark-700 text-white text-sm">← Back</button>
              <button onClick={submit} disabled={submitting || !acceptedDisclaimer}
                className="flex-1 py-2.5 rounded-xl bg-amber-500 text-dark-900 font-bold text-sm disabled:opacity-40 flex items-center justify-center gap-2 transition-all">
                {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                {submitting ? 'Submitting…' : 'Submit for review'}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

function TierEditor({ idx, tier, onChange, onPerkChange, onPerkAdd, onPerkRemove, onRemove }) {
  const accent = tier.accent || ['emerald', 'amber', 'purple'][idx] || 'emerald';
  const accentClass = { emerald: 'border-emerald-500/30', amber: 'border-amber-500/30', purple: 'border-purple-500/30' }[accent];
  return (
    <div className={`bg-dark-700/30 border ${accentClass} rounded-xl p-3 space-y-2`}>
      <div className="flex items-center gap-2">
        <input type="text" value={tier.name || ''} onChange={e => onChange('name', e.target.value)} maxLength={20}
          placeholder="Tier name"
          className="flex-1 bg-dark-800 border border-dark-600 rounded-lg px-2.5 py-1.5 text-white text-sm placeholder-gray-600 focus:outline-none" />
        <div className="flex items-center gap-1 bg-dark-800 border border-dark-600 rounded-lg px-2 py-1.5">
          <DollarSign className="w-3.5 h-3.5 text-gray-500" />
          <input type="number" value={tier.price || ''} onChange={e => onChange('price', parseFloat(e.target.value) || 0)} min={1} max={500} step={0.01}
            className="w-16 bg-transparent text-white text-sm focus:outline-none" />
          <span className="text-[10px] text-gray-500">/mo</span>
        </div>
        {onRemove && (
          <button onClick={onRemove} className="text-red-400 hover:text-red-300 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
        )}
      </div>
      <div className="space-y-1.5">
        {(tier.perks || []).map((perk, pi) => (
          <div key={pi} className="flex items-center gap-1.5">
            <span className="text-[10px] text-gray-600">•</span>
            <input type="text" value={perk} onChange={e => onPerkChange(pi, e.target.value)} maxLength={80}
              placeholder="What does this tier get?"
              className="flex-1 bg-dark-800 border border-dark-600 rounded-lg px-2 py-1 text-white text-xs placeholder-gray-600 focus:outline-none" />
            {tier.perks.length > 2 && (
              <button onClick={() => onPerkRemove(pi)} className="text-gray-500 hover:text-red-400"><X className="w-3 h-3" /></button>
            )}
          </div>
        ))}
        {tier.perks.length < 5 && (
          <button onClick={onPerkAdd} className="text-[10px] text-gray-500 hover:text-amber-400 flex items-center gap-1">
            <Plus className="w-3 h-3" /> Add perk
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Subscribe Modal (viewer POV) ─────────────────────────────────────────

// ERC-20 transfer ABI fragment for one-click pay
const USDC_TRANSFER_ABI = [{
  type: 'function', name: 'transfer', stateMutability: 'nonpayable',
  inputs: [{ name: 'to', type: 'address' }, { name: 'amount', type: 'uint256' }],
  outputs: [{ type: 'bool' }],
}];
const USDC_BALANCE_ABI = [{
  type: 'function', name: 'balanceOf', stateMutability: 'view',
  inputs: [{ name: 'account', type: 'address' }],
  outputs: [{ type: 'uint256' }],
}];

// Supported payment chains (Ethereum mainnet EXCLUDED — gas fees exceed the
// 20% platform margin on small subs; users must bridge to a cheap chain first).
const PAY_CHAINS = [
  { key: 'base',     label: 'Base',     chainId: 8453,  usdc: '0x833589fcd6edb6e08f4c7c32d4f71b54bda02913', gasEstUsd: 0.02,  icon: '🔵' },
  { key: 'polygon',  label: 'Polygon',  chainId: 137,   usdc: '0x3c499c542cef5e3811e1192ce70d8cc03d5c3359', gasEstUsd: 0.005, icon: '🟣' },
  { key: 'arbitrum', label: 'Arbitrum', chainId: 42161, usdc: '0xaf88d065e77c8cc2239327c5edb3a432268e5831', gasEstUsd: 0.10,  icon: '🔷' },
];

const BLOCKED_CHAIN_ID_MAINNET = 1;

export function SubscribeChannelModal({ room, onClose, onSubscribed }) {
  const [selectedTier, setSelectedTier] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [paymentConfirmedByUser, setPaymentConfirmedByUser] = useState(false);
  const [txHash, setTxHash] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [activated, setActivated] = useState(false);
  const [oneClickState, setOneClickState] = useState('idle'); // idle | switching | signing | confirming | error
  const [showManualPay, setShowManualPay] = useState(false);
  const [selectedChain, setSelectedChain] = useState('base'); // user's chosen chain
  const { address: walletAddress, isConnected, connector } = useAccount();
  const { data: walletClient } = useWalletClient();
  const chainId = useChainId();
  const { switchChainAsync } = useSwitchChain();

  // Read user's USDC balance on all 3 chains in parallel
  const balanceQueries = walletAddress ? PAY_CHAINS.map(c => ({
    address: c.usdc,
    abi: USDC_BALANCE_ABI,
    functionName: 'balanceOf',
    args: [walletAddress],
    chainId: c.chainId,
  })) : [];
  const { data: balanceData } = useReadContracts({
    contracts: balanceQueries,
    query: { enabled: !!walletAddress, refetchInterval: 30000 },
  });
  const usdcBalances = PAY_CHAINS.map((c, i) => {
    const raw = balanceData?.[i]?.result;
    const balance = raw ? Number(raw) / 1e6 : 0;
    return { ...c, balance };
  });

  // Auto-pick the cheapest chain where user has enough balance, once balances load
  useEffect(() => {
    if (!paymentInfo || !walletAddress || !balanceData) return;
    const required = paymentInfo.amount;
    // Order: Polygon (cheapest gas) → Base → Arbitrum
    const preferenceOrder = ['polygon', 'base', 'arbitrum'];
    for (const k of preferenceOrder) {
      const chain = usdcBalances.find(c => c.key === k);
      if (chain && chain.balance >= required) {
        if (selectedChain !== k) setSelectedChain(k);
        return;
      }
    }
    // Nobody has enough — leave selectedChain on Base for the funding hint
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentInfo?.amount, walletAddress, balanceData]);

  const tiers = room?.tiers || [];

  async function subscribe(tier) {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${API}/channel-subs/subscribe`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ channelId: room.id, tierId: tier.tierId, subscriberWallet: walletAddress || undefined }),
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || 'Subscribe failed');
      setSelectedTier(tier);
      setPaymentInfo({ ...data.paymentInstructions, subscriptionId: data.subscriptionId, resumed: !!data.resumed });
    } catch (e) {
      setError(e.message);
    }
    setSubmitting(false);
  }

  // On-chain recovery: scan last 30min of USDC transfers from this wallet to
  // treasury, auto-confirm if a matching payment is found. Used when wagmi
  // dropped the tx hash (iOS deep-link issue), or when the payment was made
  // from a separate wallet/exchange.
  async function findMyPayment() {
    if (!paymentInfo) return;
    setOneClickState('confirming');
    setError(null);
    try {
      const res = await fetch(`${API}/channel-subs/find-payment`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ subscriptionId: paymentInfo.subscriptionId, subscriberWallet: walletAddress || undefined }),
      });
      const data = await res.json();
      if (data.alreadyActive || data.found) {
        setActivated(true);
        if (data.txHash) setTxHash(data.txHash);
        if (onSubscribed) onSubscribed();
        setOneClickState('idle');
        return;
      }
      if (data.message) {
        setError(data.message + (walletAddress ? '' : ' — connect the wallet you paid from'));
      } else if (data.error) {
        setError(data.error);
      }
      setOneClickState('idle');
    } catch (e) {
      setError(e.message);
      setOneClickState('idle');
    }
  }

  // Auto-recovery on modal open with pending sub:
  //   1. If localStorage has a tx hash from a prior writeContract that iOS dropped,
  //      try to confirm with it directly (fastest path).
  //   2. Otherwise, fall back to on-chain scan from the connected wallet.
  useEffect(() => {
    if (!paymentInfo?.resumed || activated || oneClickState !== 'idle') return;

    let cancelled = false;
    (async () => {
      // Path 1: stored tx hash
      try {
        const raw = localStorage.getItem(`pendingPay:${paymentInfo.subscriptionId}`);
        if (raw) {
          const { hash } = JSON.parse(raw);
          if (hash && /^0x[a-fA-F0-9]{64}$/.test(hash)) {
            setOneClickState('confirming');
            const res = await fetch(`${API}/channel-subs/confirm-payment`, {
              method: 'POST',
              headers: authHeaders(),
              body: JSON.stringify({ subscriptionId: paymentInfo.subscriptionId, txHash: hash, chain: 'base' }),
            });
            const data = await res.json();
            if (!cancelled && res.ok && !data.error) {
              setActivated(true);
              setTxHash(hash);
              try { localStorage.removeItem(`pendingPay:${paymentInfo.subscriptionId}`); } catch {}
              if (onSubscribed) onSubscribed();
              setOneClickState('idle');
              return;
            }
            // Stored hash didn't verify (yet) — fall through to scan
            if (!cancelled) setOneClickState('idle');
          }
        }
      } catch {}

      // Path 2: on-chain scan if wallet connected
      if (!cancelled && isConnected && walletAddress) {
        findMyPayment();
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentInfo?.resumed, isConnected, walletAddress]);

  // One-click pay: wallet signs USDC.transfer directly on the SELECTED chain.
  // Backend then verifies the resulting tx on-chain and activates instantly.
  async function payWithWallet() {
    if (!walletClient || !walletAddress || !paymentInfo) return;
    const targetChain = PAY_CHAINS.find(c => c.key === selectedChain) || PAY_CHAINS[0];
    setError(null);
    setOneClickState('switching');
    try {
      if (chainId !== targetChain.chainId) {
        await switchChainAsync({ chainId: targetChain.chainId });
      }

      setOneClickState('signing');
      const amountWei = BigInt(Math.floor(paymentInfo.amount * 1e6)); // USDC has 6 decimals
      const hash = await walletClient.writeContract({
        address: targetChain.usdc,
        abi: USDC_TRANSFER_ABI,
        functionName: 'transfer',
        args: [paymentInfo.address, amountWei],
        chainId: targetChain.chainId,
      });

      // Persist immediately — protects against iOS suspending the page while
      // user is in their wallet app. On next mount we can recover from this.
      try {
        localStorage.setItem(`pendingPay:${paymentInfo.subscriptionId}`, JSON.stringify({ hash, chain: targetChain.key, ts: Date.now() }));
      } catch {}

      setOneClickState('confirming');
      const res = await fetch(`${API}/channel-subs/confirm-payment`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ subscriptionId: paymentInfo.subscriptionId, txHash: hash, chain: targetChain.key }),
      });
      const data = await res.json();

      // If receipt not on-chain yet, poll for ~30s
      let attempts = 0;
      while ((!res.ok || data.error) && (data?.error || '').includes('not found on-chain') && attempts < 10) {
        await new Promise(r => setTimeout(r, 3000));
        attempts++;
        const retry = await fetch(`${API}/channel-subs/confirm-payment`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ subscriptionId: paymentInfo.subscriptionId, txHash: hash, chain: targetChain.key }),
        });
        const retryData = await retry.json();
        if (retry.ok && !retryData.error) {
          setActivated(true);
          setTxHash(hash);
          if (onSubscribed) onSubscribed();
          setOneClickState('idle');
          return;
        }
      }
      if (!res.ok || data.error) throw new Error(data.error || `HTTP ${res.status}`);

      setActivated(true);
      setTxHash(hash);
      try { localStorage.removeItem(`pendingPay:${paymentInfo.subscriptionId}`); } catch {}
      if (onSubscribed) onSubscribed();
      setOneClickState('idle');
    } catch (e) {
      const msg = e?.shortMessage || e?.message || 'Payment failed';
      setError(
        msg.includes('User rejected') || msg.includes('User denied') ? 'Payment cancelled in wallet' :
        msg.includes('insufficient funds') || msg.includes('exceeds balance') ? 'Insufficient USDC in your wallet on Base' :
        msg
      );
      setOneClickState('error');
    }
  }

  async function abandonAndRetry() {
    if (!paymentInfo?.subscriptionId) return;
    setSubmitting(true);
    setError(null);
    try {
      await fetch(`${API}/channel-subs/cancel`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ subscriptionId: paymentInfo.subscriptionId }),
      });
      // Reset modal back to tier picker
      setPaymentInfo(null);
      setSelectedTier(null);
      setTxHash('');
      setActivated(false);
      setPaymentConfirmedByUser(false);
    } catch (e) {
      setError(e.message);
    }
    setSubmitting(false);
  }

  function copyAddress() {
    if (!paymentInfo?.address) return;
    navigator.clipboard.writeText(paymentInfo.address).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  if (paymentInfo) {
    return (
      <Modal onClose={onClose} title={`Subscribe to ${room.name}`}>
        <div className="space-y-4">
          {paymentInfo.resumed && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 text-[11px] text-amber-200">
                <p className="font-bold text-amber-300">Resuming previous subscription</p>
                <p className="mt-0.5">You started this subscription earlier but never confirmed the payment. Paste your tx hash below if you already sent USDC, or click "Cancel & start over" to abandon and try fresh.</p>
              </div>
            </div>
          )}
          <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4">
            <p className="text-xs text-emerald-300 font-medium mb-1">Send USDC to activate</p>
            <p className="text-2xl font-bold text-white">${paymentInfo.amount} <span className="text-sm text-gray-500">USDC on Base</span></p>
          </div>
          <div className="space-y-2">
            <label className="text-[10px] text-gray-500 uppercase tracking-wider">Treasury address (Base network)</label>
            <div className="bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 flex items-center justify-between gap-2">
              <code className="text-xs text-white font-mono truncate">{paymentInfo.address}</code>
              <button onClick={copyAddress} className="text-amber-400 hover:text-amber-300 flex-shrink-0">
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-3">
            <p className="text-[11px] text-amber-300 font-medium mb-1">⚠ Important</p>
            <ul className="text-[11px] text-amber-200/80 space-y-1 pl-3 list-disc">
              <li>Use the <span className="font-bold">Base network</span> only (not Ethereum mainnet)</li>
              <li>Send <span className="font-bold">USDC</span>, not ETH or other tokens</li>
              <li>Once confirmed on-chain, your subscription activates within ~1 minute</li>
              <li>Access lasts 30 days from activation. Auto-renew is OFF by default.</li>
            </ul>
          </div>
          {!activated ? (
            <>
              {/* ─── MAINNET WARNING ──────────────────────────────────────── */}
              {isConnected && chainId === BLOCKED_CHAIN_ID_MAINNET && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 flex gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="text-[11px] text-red-200">
                    <p className="font-bold text-red-300">Ethereum mainnet not supported</p>
                    <p className="mt-0.5">Mainnet gas fees ($5-15) exceed subscription value. Switch to Base, Polygon, or Arbitrum — same wallet, dramatically cheaper. Bridge USDC at <a href="https://bridge.base.org" target="_blank" rel="noopener" className="text-amber-400 underline">bridge.base.org</a> or buy directly via Coinbase.</p>
                  </div>
                </div>
              )}

              {/* ─── ONE-CLICK PAY (connected wallet) ──────────────────────── */}
              {isConnected && walletAddress ? (
                <div className="bg-gradient-to-br from-emerald-500/10 to-cyan-500/10 border border-emerald-500/30 rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                      <p className="text-xs font-bold text-emerald-300">Pay with your wallet</p>
                    </div>
                    <p className="text-[10px] text-gray-500 font-mono">{walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}</p>
                  </div>

                  {/* Chain selector showing balance + gas estimate per chain */}
                  <div>
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5">Pay from which chain?</p>
                    <div className="space-y-1.5">
                      {usdcBalances.map(c => {
                        const enough = c.balance >= paymentInfo.amount;
                        const recommended = ['polygon', 'base', 'arbitrum'].indexOf(c.key) ===
                          ['polygon', 'base', 'arbitrum'].findIndex(k => {
                            const x = usdcBalances.find(b => b.key === k);
                            return x && x.balance >= paymentInfo.amount;
                          });
                        return (
                          <button key={c.key} onClick={() => enough && setSelectedChain(c.key)}
                            disabled={!enough}
                            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg border text-left transition-all ${
                              selectedChain === c.key && enough ? 'bg-emerald-500/20 border-emerald-500/50' :
                              enough ? 'bg-dark-800/50 border-dark-600 hover:border-dark-500' :
                              'bg-dark-800/30 border-dark-700 opacity-50 cursor-not-allowed'
                            }`}>
                            <span className="text-base">{c.icon}</span>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-white">
                                {c.label}
                                {recommended && enough && <span className="ml-1.5 text-[9px] text-emerald-400 font-bold">RECOMMENDED</span>}
                              </p>
                              <p className="text-[10px] text-gray-500">
                                Gas: ~${c.gasEstUsd.toFixed(3)} · You have: <span className={enough ? 'text-emerald-400' : 'text-amber-400'}>${c.balance.toFixed(2)} USDC</span>
                              </p>
                            </div>
                            {selectedChain === c.key && enough && <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />}
                          </button>
                        );
                      })}
                    </div>
                    {!usdcBalances.some(c => c.balance >= paymentInfo.amount) && (
                      <p className="text-[11px] text-amber-400 mt-2">
                        ⚠ You don't have ${paymentInfo.amount} USDC on any supported chain. <br/>
                        Buy on Coinbase → withdraw to Base, or bridge from another chain.
                      </p>
                    )}
                  </div>

                  <button onClick={payWithWallet}
                    disabled={oneClickState === 'switching' || oneClickState === 'signing' || oneClickState === 'confirming' || !usdcBalances.find(c => c.key === selectedChain)?.balance || usdcBalances.find(c => c.key === selectedChain)?.balance < paymentInfo.amount}
                    className="w-full py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-dark-900 font-bold text-sm disabled:opacity-50 flex items-center justify-center gap-2 transition-all">
                    {oneClickState === 'switching' ? <><Loader2 className="w-4 h-4 animate-spin" /> Switching to {PAY_CHAINS.find(c => c.key === selectedChain)?.label}…</> :
                     oneClickState === 'signing'   ? <><Loader2 className="w-4 h-4 animate-spin" /> Confirm in wallet…</> :
                     oneClickState === 'confirming'? <><Loader2 className="w-4 h-4 animate-spin" /> Verifying on-chain…</> :
                     <><DollarSign className="w-4 h-4" /> Pay ${paymentInfo.amount} USDC on {PAY_CHAINS.find(c => c.key === selectedChain)?.label}</>}
                  </button>
                </div>
              ) : (
                <div className="bg-gradient-to-br from-emerald-500/10 to-cyan-500/10 border border-emerald-500/30 rounded-xl p-4 space-y-3">
                  <p className="text-xs font-bold text-emerald-300 text-center">⚡ Connect wallet for 1-click pay</p>
                  <p className="text-[11px] text-gray-400 text-center">No more copy-pasting addresses or tx hashes — your wallet handles everything.</p>
                  <ConnectButton.Custom>
                    {({ openConnectModal }) => (
                      <button onClick={openConnectModal}
                        className="w-full py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-dark-900 font-bold text-sm flex items-center justify-center gap-2 transition-all">
                        <Zap className="w-4 h-4" /> Connect Wallet
                      </button>
                    )}
                  </ConnectButton.Custom>
                  <p className="text-[10px] text-gray-600 text-center">MetaMask, Rainbow, Coinbase Wallet, WalletConnect, and more</p>
                </div>
              )}

              {/* ─── RECOVERY: scan for payment that already happened ──────── */}
              {isConnected && walletAddress && (
                <button onClick={findMyPayment}
                  disabled={oneClickState === 'confirming'}
                  className="w-full py-2 rounded-xl bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 text-xs font-bold disabled:opacity-50 flex items-center justify-center gap-2 transition-all">
                  {oneClickState === 'confirming' ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Scanning Base for your payment…</> :
                   <><RefreshCw className="w-3.5 h-3.5" /> Already sent USDC? Find my payment</>}
                </button>
              )}

              {/* ─── MANUAL PAY (collapsible fallback) ─────────────────────── */}
              <button onClick={() => setShowManualPay(!showManualPay)}
                className="w-full text-[11px] text-gray-500 hover:text-white py-1 flex items-center justify-center gap-1">
                <span className={`transition-transform inline-block ${showManualPay ? 'rotate-90' : ''}`}>▶</span>
                Or paste tx hash manually (from another wallet / exchange)
              </button>

              {showManualPay && (
                <div className="bg-dark-700/30 border border-dark-700/50 rounded-xl p-3 space-y-3">
                  <p className="text-[10px] text-gray-500">
                    Send USDC from any source (Coinbase exchange, hardware wallet, etc.) to the treasury address shown above, then paste your tx hash below to activate.
                  </p>
                  <div>
                    <label className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5 block">Transaction hash</label>
                    <input type="text" value={txHash} onChange={e => setTxHash(e.target.value.trim())} maxLength={66}
                      placeholder="0x... (64 hex chars after 0x)"
                      className="w-full bg-dark-800 border border-dark-600 rounded-lg px-3 py-2 text-white text-xs font-mono placeholder-gray-600 focus:border-amber-400 focus:outline-none" />
                  </div>
                  <button onClick={async () => {
                    if (!/^0x[a-fA-F0-9]{64}$/.test(txHash)) {
                      setError('Invalid tx hash — must be 0x followed by 64 hex characters');
                      return;
                    }
                    setConfirming(true); setError(null);
                    try {
                      const res = await fetch(`${API}/channel-subs/confirm-payment`, {
                        method: 'POST',
                        headers: authHeaders(),
                        body: JSON.stringify({ subscriptionId: paymentInfo.subscriptionId, txHash, chain: 'base' }),
                      });
                      const data = await res.json();
                      if (!res.ok || data.error) throw new Error(data.error || 'Confirmation failed');
                      setActivated(true);
                      if (onSubscribed) onSubscribed();
                    } catch (e) {
                      setError(e.message);
                    }
                    setConfirming(false);
                  }} disabled={confirming || !txHash}
                    className="w-full py-2 rounded-lg bg-amber-500/80 hover:bg-amber-500 text-dark-900 font-bold text-xs disabled:opacity-40 flex items-center justify-center gap-2">
                    {confirming ? <><Loader2 className="w-3 h-3 animate-spin" /> Verifying…</> : <>Verify & activate</>}
                  </button>
                </div>
              )}

              <button onClick={abandonAndRetry} disabled={submitting || confirming || oneClickState !== 'idle'}
                className="w-full py-2 rounded-xl text-[10px] text-gray-500 hover:text-red-400 transition-colors">
                Cancel & start over (abandons this pending payment)
              </button>
            </>
          ) : (
            <>
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 text-center">
                <div className="w-12 h-12 mx-auto bg-emerald-500/20 rounded-full flex items-center justify-center mb-2">
                  <Check className="w-6 h-6 text-emerald-400" />
                </div>
                <p className="text-sm font-bold text-emerald-300">Subscription activated!</p>
                <p className="text-xs text-gray-400 mt-1">Access lasts 30 days. Creator gets paid 80% automatically.</p>
              </div>
              <AutoRenewPrompt
                subscriptionId={paymentInfo.subscriptionId}
                pricePerMonth={paymentInfo.amount}
                onDone={() => { if (onSubscribed) onSubscribed(); onClose(); }} />
              <button onClick={() => { if (onSubscribed) onSubscribed(); onClose(); }}
                className="w-full py-2 rounded-xl text-xs text-gray-500 hover:text-white">
                Skip auto-renew — I'll renew manually each month
              </button>
            </>
          )}
        </div>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose} title={`Subscribe to ${room.name}`}>
      <div className="space-y-3">
        <p className="text-xs text-gray-400">{room.description}</p>

        {tiers.length === 0 && (
          <div className="bg-dark-700/40 rounded-xl p-4 text-center text-xs text-gray-500">
            This channel is not yet configured for subscriptions.
          </div>
        )}

        {tiers.map((tier, i) => {
          const accent = ['emerald', 'amber', 'purple'][i] || 'emerald';
          const accentClass = {
            emerald: 'border-emerald-500/30 hover:border-emerald-500/60',
            amber: 'border-amber-500/30 hover:border-amber-500/60',
            purple: 'border-purple-500/30 hover:border-purple-500/60',
          }[accent];
          const accentText = { emerald: 'text-emerald-400', amber: 'text-amber-400', purple: 'text-purple-400' }[accent];
          return (
            <div key={tier.tierId} className={`bg-dark-700/30 border ${accentClass} rounded-xl p-4 transition-all`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="text-white font-bold">{tier.name}</p>
                  <p className={`text-xl font-bold ${accentText}`}>${tier.price}<span className="text-xs text-gray-500">/mo</span></p>
                </div>
                <button onClick={() => subscribe(tier)} disabled={submitting}
                  className={`px-4 py-2 rounded-lg text-xs font-bold disabled:opacity-40 ${
                    accent === 'emerald' ? 'bg-emerald-500 text-dark-900' : accent === 'amber' ? 'bg-amber-500 text-dark-900' : 'bg-purple-500 text-white'
                  }`}>
                  {submitting ? <Loader2 className="w-3 h-3 animate-spin inline" /> : 'Subscribe'}
                </button>
              </div>
              <ul className="space-y-1 mt-2">
                {(tier.perks || []).map((p, pi) => (
                  <li key={pi} className="text-xs text-gray-400 flex items-start gap-1.5">
                    <Check className={`w-3 h-3 ${accentText} flex-shrink-0 mt-0.5`} /> {p}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2 text-xs text-red-400 flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5" /> {error}
          </div>
        )}
      </div>
    </Modal>
  );
}

// ─── Auto-renew on-chain approval prompt ──────────────────────────────────
// Shown after a successful first-month payment. User signs ONE approve() tx
// for N months × price USDC, then our hourly worker debits monthly.

const USDC_BASE = '0x833589fcd6edb6e08f4c7c32d4f71b54bda02913';
const ERC20_APPROVE_ABI = [{
  type: 'function', name: 'approve', stateMutability: 'nonpayable',
  inputs: [{ name: 'spender', type: 'address' }, { name: 'amount', type: 'uint256' }],
  outputs: [{ type: 'bool' }],
}];

export function AutoRenewPrompt({ subscriptionId, pricePerMonth, onDone }) {
  const { address: walletAddress, isConnected } = useAccount();
  const { data: walletClient } = useWalletClient();
  const chainId = useChainId();
  const { switchChainAsync } = useSwitchChain();
  const [months, setMonths] = useState(6);
  const [spenderInfo, setSpenderInfo] = useState(null);
  const [state, setState] = useState('idle'); // idle | approving | recording | done | error
  const [error, setError] = useState(null);
  const [txHash, setTxHash] = useState(null);

  useEffect(() => {
    getAutoRenewSpender().then(setSpenderInfo).catch(() => setSpenderInfo({ available: false }));
  }, []);

  async function handleEnable() {
    setError(null);
    if (!isConnected || !walletClient || !walletAddress) {
      setError('Connect your wallet to enable auto-renew');
      return;
    }
    if (!spenderInfo?.available) {
      setError('Auto-renew is not available on this server yet');
      return;
    }
    setState('approving');
    try {
      // Ensure we're on Base
      if (chainId !== 8453) {
        await switchChainAsync({ chainId: 8453 });
      }
      const amountUsdc = BigInt(Math.floor(pricePerMonth * months * 1e6)); // 6 decimals
      const hash = await walletClient.writeContract({
        address: USDC_BASE,
        abi: ERC20_APPROVE_ABI,
        functionName: 'approve',
        args: [spenderInfo.spenderAddress, amountUsdc],
      });
      setTxHash(hash);
      setState('recording');

      await enableAutoRenew({
        subscriptionId,
        subscriberWallet: walletAddress,
        approvalTxHash: hash,
        maxMonths: months,
        chain: 'base',
      });
      setState('done');
      if (onDone) setTimeout(onDone, 1500);
    } catch (e) {
      const msg = e?.shortMessage || e?.message || 'Approval failed';
      setError(msg.includes('User rejected') ? 'Approval cancelled in wallet' : msg);
      setState('error');
    }
  }

  if (state === 'done') {
    return (
      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 text-center">
        <div className="w-12 h-12 mx-auto bg-emerald-500/20 rounded-full flex items-center justify-center mb-2">
          <Check className="w-6 h-6 text-emerald-400" />
        </div>
        <p className="text-sm font-bold text-emerald-300">Auto-renew enabled</p>
        <p className="text-xs text-gray-400 mt-1">Next ${pricePerMonth} USDC debits in 30 days. No card required.</p>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-amber-500/5 to-orange-500/5 border border-amber-500/20 rounded-xl p-4 space-y-3">
      <div className="flex items-start gap-2">
        <RefreshCw className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-bold text-white">Enable on-chain auto-renew? <span className="text-[10px] font-normal text-gray-500">(optional)</span></p>
          <p className="text-[11px] text-gray-400 mt-0.5">One signature approves the platform to debit <span className="text-amber-400 font-mono">${pricePerMonth} USDC</span> monthly for up to N months. No card. No KYC. Cancel anytime by revoking approval.</p>
        </div>
      </div>

      <div>
        <label className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5 block">Approve for how many months?</label>
        <div className="grid grid-cols-3 gap-2">
          {[3, 6, 12].map(m => (
            <button key={m} onClick={() => setMonths(m)}
              className={`py-2 rounded-lg text-xs font-semibold border transition-all ${
                months === m ? 'bg-amber-500/20 border-amber-500/50 text-amber-300' : 'bg-dark-700 border-dark-600 text-gray-400 hover:border-dark-500'
              }`}>
              {m} mo<br />
              <span className="text-[9px] font-mono opacity-70">${(pricePerMonth * m).toFixed(2)}</span>
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 text-[11px] text-red-400 flex items-center gap-2">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" /> {error}
        </div>
      )}

      <button onClick={handleEnable} disabled={state === 'approving' || state === 'recording' || !spenderInfo?.available}
        className="w-full py-2.5 rounded-xl bg-amber-500 text-dark-900 font-bold text-sm disabled:opacity-40 flex items-center justify-center gap-2 transition-all">
        {state === 'approving' ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Sign in wallet…</> :
         state === 'recording' ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Recording approval…</> :
         !spenderInfo?.available ? <>Auto-renew unavailable yet</> :
         <><Shield className="w-3.5 h-3.5" /> Approve {months} months</>}
      </button>

      <p className="text-[10px] text-gray-600 text-center">
        Auto-renew uses standard ERC-20 allowance. You retain full custody. Revoke via your wallet or in Settings → Subscriptions.
      </p>
    </div>
  );
}

// ─── Locked Channel Panel (shown in place of messages) ────────────────────

export function PaidChannelLockedPanel({ room, onSubscribe }) {
  const tiers = room?.tiers || [];
  const lowest = tiers.length ? Math.min(...tiers.map(t => t.price)) : null;
  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="max-w-md text-center">
        <div className="w-16 h-16 mx-auto bg-amber-500/10 rounded-full flex items-center justify-center mb-4">
          <Lock className="w-8 h-8 text-amber-400" />
        </div>
        <h3 className="text-xl font-bold text-white mb-2">{room.name} is a premium channel</h3>
        <p className="text-sm text-gray-400 mb-6">
          {lowest != null ? <>Starting at <span className="text-amber-400 font-bold">${lowest}/mo</span> in USDC. Cancel anytime.</> : <>Subscribe to unlock messages.</>}
        </p>
        <button onClick={onSubscribe} className="px-6 py-3 rounded-xl bg-amber-500 text-dark-900 font-bold text-sm hover:bg-amber-400 transition-all flex items-center gap-2 mx-auto">
          <TrendingUp className="w-4 h-4" /> View tiers & subscribe
        </button>
      </div>
    </div>
  );
}

// ─── Modal shell ──────────────────────────────────────────────────────────

function Modal({ onClose, children, title }) {
  return (
    <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-dark-800 rounded-2xl border border-dark-700 max-w-md w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {title && (
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold text-white">{title}</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}
